<template>
  <LoadingIndicator 
    v-if="loading"
    topic="Evaluation"
  />
  <div v-else>
    <!-- <h1 class="h4 mb-4">Edit Evaluation</h1> -->
    <b-form id="surveyForm" @submit.prevent="handleSurveyNameChange">
      <b-form-group
        id="surveyNameGroup"
        description="The name of the evaluation you want project respondents to see."
        label="Evaluation Name"
        label-for="surveyName">
        <b-form-input
          v-model.trim="survey.surveyName"
          id="surveyName"
          :state="errors.length == 0 ? null : isValidSurveyName()"
          maxlength="100"
          @blur="handleSurveyNameChange"
        >
          {{survey.surveyName}}
        </b-form-input>
        <div 
          v-if="nameUpdatedSuccess"
          class="text-success"
        >Success!</div>
      </b-form-group>
    </b-form>
    <BaseMessageBlock :messages="surveyNameError"></BaseMessageBlock>
    <BaseDraggableList
      v-if="customQuestions"
      :listItems="customQuestions"
      class="mb-3"
      @updateItemsOrder="updateQuestionsOrder"
    >
      <template v-slot:default="props">
        <b-row class="align-items-center">
          <b-col cols="auto" class="flex-grow-1 ">
            {{ props.item.text }}
          </b-col>
          <b-col 
            v-if="!isSafetyQuestion(props.item)"
            cols="auto" 
            class="text-left pl-0 ml-0"
          >
            <b-button 
              variant="link"
              class="p-0"
              @click="handleEditQuestion(props.item.questionId)"
            >
              <b-icon icon="pencil-square" font-scale="1.5"></b-icon>
            </b-button>
          </b-col>
          <b-col 
            v-if="!isSafetyQuestion(props.item)"
            cols="auto" 
            class="text-left pl-0 ml-0"
          >
            <b-button 
              variant="link"
              class="p-0"
              @click="handleDeleteQuestion(props.item)"
            >
              <b-icon icon="trash-fill" font-scale="1.5"></b-icon>
            </b-button>
          </b-col>
          <b-col
            cols="auto" 
            class="text-left pl-0 pr-2 ml-0"
          >
            <b-button 
              variant="link"
              class="p-0 pointer-events-none"
              @click.prevent
            >
              <b-icon icon="list" font-scale="1.5"></b-icon>
            </b-button>
          </b-col>
        </b-row>
      </template>
    </BaseDraggableList>
    <b-row>
      <b-col>
        <b-button
          target="_blank" 
          class="mr-2"
          variant="primary"
          @click="showNewQuestionModal"
        >
          <b-icon icon="plus-circle-fill" class="mr-2"></b-icon> Create new question
        </b-button>
        <b-button
          v-if="hasSafetyQuestions"
          variant="primary"
          @click="removeSafetyQuestions"
        >
          <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon> Remove safety questions  
        </b-button>
        <b-button 
          v-else
          variant="primary"
          @click="addSafetyQuestions"
        >
          <b-icon icon="exclamation-triangle-fill" class="mr-2"></b-icon> Add safety questions
        </b-button>
      </b-col>
    </b-row>
    <BaseMessageBlock :messages="uniqueErrors"></BaseMessageBlock>
    <b-modal
      ref="delete-question-modal"
      @ok="deleteQuestion(deletingQuestion.questionId)"
      @canel="resetDeletingQuestion"
      ok-title="Yes, I'm sure."
      cancel-title="Cancel"
      title="Delete Confirmation"
    >
      <p 
        v-if="deletingQuestion"
        class="my-4"
      >
        Are you sure you want to delete question "{{deletingQuestion.text}}"?
      </p>
    </b-modal>
    <CreateQuestionModal
      ref="new-question-modal"
      :survey="survey"
      @submit="addNewQuestion"
      @submitTemplates="addTemplateQuestions"
    ></CreateQuestionModal>
    <EditQuestionModal
      ref="edit-question-modal"
      :surveyId="survey.surveyId"
      :questionId="editingQuestionId"
      @submit="updateExistingQuestion"
      @reset="resetEditingQuestionId"
    ></EditQuestionModal>
  </div>
</template>

<script>
import axios from "axios";
import ProjectActionBar from "@/sub-components/ProjectActionBar.vue";
import EditQuestionModal from "@/modal-components/EditQuestionModal.vue";
import CreateQuestionModal from "@/modal-components/CreateQuestionModal.vue";
import EditQuestionForm from "@/form-components/EditQuestionForm.vue";
import BaseDraggableList from "@/components/BaseDraggableList.vue";

export default {
  components: {
    ProjectActionBar,
    EditQuestionModal,
    CreateQuestionModal,
    EditQuestionForm,
    BaseDraggableList
  },
  props: {
    survey: { type: Object, default: null },
  },
  data() {
    return {
      errors: [],
      surveyNameError: [],
      dataLoaded: false,
      loading: false,
      sending: false,
      project: null,
      projectId: null,
      editingQuestionId: '',
      deletingQuestion: null,
      customQuestions: null,
      nameUpdatedSuccess: false,
    };
  },
  watch: {
    survey(survey) {
      // this.customQuestions = survey.questions;
      this.setupQuestions();
    }
  },
  mounted() {
    this.setupQuestions();
  },

  methods: {
    setupQuestions() {
      this.loading = true;
      this.customQuestions = this.survey.questions;
      this.sortQuestions();
      this.customQuestions.forEach(question => {
        this.loadAnswerOptions(question);
      });
    },
    loadAnswerOptions(question) {
      axios
        .get(`${this.$api_url}/questions/${question.questionId}`,
          this.getReqConfig()
        )
        .then(response => {
          question.answerOptions = response.data.answerOptions;

          question.answerOptions.sort((a, b) => this.$helpers.compareAlpha(a.answerIndex, b.answerIndex));

          if ( this.questionsCount === this.questionsWithAnswersCount ) {
            this.loading = false;
          }
        })
        .catch(err => {
          this.errors.push(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    sortQuestions() {
      this.customQuestions.sort((a, b) => this.$helpers.compareInt(a.sortOrder, b.sortOrder));
    },
    updateQuestionsOrder(questions) {
      // this.customQuestions = questions.map((question, index) => {
      //   question.sortOrder = index;
      //   return question;
      // });
      this.customQuestions = questions;
      this.renumberQuestionsOrder(0);
      this.updateAllQuestions();
    },
    showNewQuestionModal() {
      this.$refs['new-question-modal'].show();
    },
    addNewQuestion(question) {
      this.customQuestions.push(question);
      this.sortQuestions();
    },
    addTemplateQuestions(newQuestions) {
      this.customQuestions = this.customQuestions.concat(newQuestions);
    },
    handleEditQuestion(questionId) {
      this.editingQuestionId = questionId;
      this.showEditQuestionModal();
    },
    showEditQuestionModal() {
      this.$refs['edit-question-modal'].show();
    },
    updateExistingQuestion(updatedQuestion) {
      this.customQuestions = this.customQuestions.map(q => {
        if (q.questionId === updatedQuestion.questionId) {
          return updatedQuestion;
        }

        return q;
      });
      this.editingQuestionId = '';
    },
    handleDeleteQuestion(question) {
      this.deletingQuestion = {...question};
      this.showDeleteQuestionModal();
    },
    showDeleteQuestionModal() {
      this.$refs['delete-question-modal'].show();
    },
    deleteQuestion(questionId) {
      let questionIndex = this.customQuestions.findIndex(q => q.questionId === questionId);
      this.customQuestions[questionIndex].willBeDeleted = true;

      this.errors = [];
      axios
        .delete(`${this.$api_url}/question/${questionId}`, this.getReqConfig())
        .then(() => {
          questionIndex = this.customQuestions.findIndex(q => q.questionId === questionId);
          const wasLastQuestion = questionIndex === this.customQuestions.length;
          
          this.customQuestions.splice(questionIndex, 1);
          if (!wasLastQuestion) {
            this.renumberQuestionsOrder();
            this.updateAllQuestions();
          }
        })
        .catch(err => {
          this.errors.push(...this.$helpers.formatErrors(err));
        })
        .finally(() => {
        });
    },
    addSafetyQuestions() {
      this.renumberQuestionsOrder(2);

      const innovationQuestionId = this.$helpers.generateUUID(),
        opportunityQuestionId = this.$helpers.generateUUID();
      this.customQuestions.push({
        surveyId: this.survey.surveyId,
        questionId: innovationQuestionId,
        text: "Have you actively incorporated any safety innovations on the project?",
        type: "Safety Innovation",
        required: false,
        commentTitle: "Safety Innovation",
        sortOrder: 0,
        answerOptions: [
        {
          answerOptionId: this.$helpers.generateUUID(),
          // questionId: innovationQuestionId,
          answerIndex: 'Yes',
          answerText: 'Yes',
          answerScore: -1,
          commentRequired: true
        },
        {
          answerOptionId: this.$helpers.generateUUID(),
          // questionId: innovationQuestionId,
          answerIndex: 'No',
          answerText: 'No',
          answerScore: -1,
          commentRequired: false
        }]
      });

      this.customQuestions.push({
        surveyId: this.survey.surveyId,
        questionId: opportunityQuestionId,
        text: "Do you have an idea that would improve worker safety?",
        type: "Safety Opportunity",
        required: false,
        commentTitle: "Safety Opportunity",
        sortOrder: 1,
        answerOptions: [
        {
          answerOptionId: this.$helpers.generateUUID(),
          // questionId: opportunityQuestionId,
          answerIndex: 'Yes',
          answerText: 'Yes',
          answerScore: -1,
          commentRequired: true
        },
        {
          answerOptionId: this.$helpers.generateUUID(),
          // questionId: opportunityQuestionId,
          answerIndex: 'No',
          answerText: 'No',
          answerScore: -1,
          commentRequired: false
        }]
      });

      this.sortQuestions();
      this.updateAllQuestions();
    },
    removeSafetyQuestions() {
      const safetyQuestionIds = this.customQuestions.filter(question => this.isSafetyQuestion(question))
        .map(question => question.questionId);
      safetyQuestionIds.forEach(questionId => {
        this.deleteQuestion(questionId);
      });
      // this.customQuestions = this.customQuestions.filter(q => !this.isSafetyQuestion(q));
      // this.renumberQuestionsOrder(0);
      // this.sortQuestions();
      // this.updateAllQuestions();
    },
    isSafetyQuestion(question) {
      return question.type === "Safety Innovation" || question.type === "Safety Opportunity";
    },
    updateAllQuestions() {
      this.errors = [];
      if (this.validateQuestions()) {
        // if (this.survey.surveyId == "") {
        //   this.survey.surveyId = this.$helpers.generateUUID();
        // }
        // this.customQuestions.forEach(q => {
        //   if (q.surveyId == null) {
        //     q.questionId = this.$helpers.generateUUID();
        //     q.surveyId = this.survey.surveyId;
        //     q.answerOptions.forEach(ao => {
        //       ao.answerOptionId = this.$helpers.generateUUID();
        //       ao.questionId = q.questionId;
        //     });
        //   }
        // });
        // this.survey.hasQuestions = true;
        this.sending = true;

        this.customQuestions.forEach((question) => {
          if (question.willBeDeleted) {
            return;
          }
          this.updateQuestion(question);
        });
      }
    },
    validateQuestions() {
      const initErrors = this.errors.length;

      if (this.customQuestions.length === 0) {
        this.errors.push({ message: `You must include at least one question in this evaluation.` });
      }
      // if (this.customQuestions.some(q => isNaN(q.sortOrder))){
      //   this.errors.push({ message: "Question order input must be a number" });
      // } else if (this.customQuestions.some(q => q.sortOrder < 0)) {
      //   this.errors.push({ message: "Question order input must be greater than 0" });
      // }

      return initErrors === this.errors.length;
    },
    isValidSurveyName() {
      return this.survey.surveyName.length >= 5;
    },
    updateQuestion(question) {
      axios
        .put(`${this.$api_url}/question/add`, question, this.getReqConfig())
        .then(() => {
          question.isQuestionSubmitted = true;
          const submittedQuestions = this.customQuestions.filter(q => q.isQuestionSubmitted);
          if (this.customQuestions.length === submittedQuestions.length) {
            this.sending = false;
          }
        })
        .catch(err => {
          if (err.response && err.response.data) {
            this.errors.push(...err.response.data.map(e => ({message: e})));
          } else {
            this.errors.push(err);
          }
        })
        .finally(() => {
        });
    },
    handleSurveyNameChange() {
      if ( !this.isValidSurveyName() ) {
        this.surveyNameError = [{ message: "Please enter a name of 5 or more characters." }];
        return;
        
      } else {
        this.surveyNameError = [];
        this.updateSurveyName();
      }
      
    },
    updateSurveyName() {
      axios
        .put(
          `${this.$api_url}/surveys/add`,
          this.survey,
          this.getReqConfig()
        )
        .then(() => {
          this.nameUpdatedSuccess = true;
          window.setTimeout(() => {
            this.nameUpdatedSuccess = false;
          },1500);
        })
        .catch(err => {
          this.errors.push(err);
        });
    },
    renumberQuestionsOrder(startIndex = 0) {
      this.customQuestions.forEach(q => {
        q.sortOrder = startIndex++;
      });
    },
    resetEditingQuestionId() {
      this.editingQuestionId = '';
    },
    resetDeletingQuestion() {
      this.deletingQuestion = null;
    },
  },
  computed: {
    uniqueErrors() {
      return Array.from(new Set(this.errors));
    },
    hasSafetyQuestions() {
      if (!this.customQuestions) {
        return false;
      }
      return this.customQuestions.some(q => q.type === "Safety Innovation") 
        && this.customQuestions.some(q => q.type === "Safety Opportunity");
    },
    isMissingSurvey() {
      if (!this.customQuestions) {
        return false;
      }
      return this.customQuestions.length === 0;
    },
    questionsWithAnswersCount() {
      if (!this.customQuestions) {
        return 0;
      }
      return this.customQuestions.filter(q => q.answerOptions && q.answerOptions.length > 0).length;
    },
    questionsCount() {
      if (!this.customQuestions) {
        return 0;
      }
      return this.customQuestions.length;
    },
  }
};
</script>

<style scoped>
.bordered {
  border: #555 1px solid;
  border-radius: 5px;
  padding: 0.25em;
}
.bg-light-blue {
  background-color: rgb(221, 241, 248);
}
.modal-body {
  width: auto;
  height: auto;
  max-height: 70vh;
  overflow-y: auto;
}
.surveyTitle {
  font-size: 1.75em;
  line-height: 1em;
  color: #555;
}
</style>

